import React, { useState, useEffect, useRef } from 'react';
import '../styles/OptionsTable.css';
import ImageUploadField from './ReusableComponent/ImageUploadField';
import RichTextEditor from './ReusableComponent/RichTextEditor';
import { isHtmlEmpty, useHighlightCode } from './utils/utility';

const AddOptionsTable = ({ questionId, openDialogExternally, setOpenDialogExternally, onOptionsChange }) => {
  const [options, setOptions] = useState(() => {
    const savedOptions = localStorage.getItem('tempOptions');
    return savedOptions ? JSON.parse(savedOptions) : [];
  });

  const [option, setOption] = useState({
    option: '',
    optionAttachment: '',
    isCorrect: false,
    questionId: questionId || null,
  });

  const [showDialog, setShowDialog] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  // Errors object to track validation errors
  const [errors, setErrors] = useState({});
  const optionsTableRef = useRef(null);
  useHighlightCode(optionsTableRef, [options]);

  useEffect(() => {
    if (openDialogExternally) {
      setShowDialog(true);
      setOpenDialogExternally(false); // reset trigger
    }
  }, [openDialogExternally, setOpenDialogExternally]);

  const validateOption = () => {
    let tempErrors = {};
    if (isHtmlEmpty(option.option) && !option.optionAttachment)
      tempErrors.option = "Option text or image is required";
    return tempErrors;
  };

  const handleAddOption = () => {
    const validationErrors = validateOption();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setShowPopup(true);
      return;
    }

    const updatedOptions = [...options, option];
    setOptions(updatedOptions);
    localStorage.setItem('tempOptions', JSON.stringify(updatedOptions));

    setOption({
      option: '',
      optionAttachment: '',
      isCorrect: false,
      questionId: questionId,
    });
    setErrors({});
    setShowDialog(false);
    if (onOptionsChange) onOptionsChange(updatedOptions);
  };

  useEffect(() => {
    setOption((prevOption) => ({
      ...prevOption,
      questionId: questionId || prevOption.questionId,
    }));
  }, [questionId]);

  return (
    <div className="option-container">
      {options.length > 0 && (<div className="option-header">
        <h3>Options</h3>
      </div>
      )}

      {/* Add Option Dialog */}
      {showDialog && (
        <div className="option-dialog-backdrop">
          <div className="option-dialog-box">
            <h4>Add New Option</h4>

            <div className="option-form-group">
              <label htmlFor="option">Option</label>
              <RichTextEditor
                toolbar="basic"
                height={120}
                value={option.option}
                onChange={(html) => {
                  setOption((prev) => ({ ...prev, option: html }));
                  setErrors((prev) => ({ ...prev, option: '' }));
                }}
                placeholder="Enter your option"
              />
              {errors.option && <div className="error-text">{errors.option}</div>}
            </div>
            <div className="option-form-group">
              <label htmlFor="optionAttachment">Option Image (optional)</label>
              <ImageUploadField
                value={option.optionAttachment}
                onChange={(url) => setOption((prev) => ({ ...prev, optionAttachment: url }))}
              />
            </div>
            <div className="option-form-group" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                id="isCorrect"
                name="isCorrect"
                checked={!!option.isCorrect}
                onChange={(e) => setOption((prev) => ({ ...prev, isCorrect: e.target.checked }))}
                style={{ width: 16, height: 16, cursor: "pointer" }}
              />
              <label htmlFor="isCorrect" style={{ margin: 0, cursor: "pointer" }}>Mark as Correct Answer</label>
            </div>

            <div className="option-dialog-actions">
              <button type='button' onClick={handleAddOption}>Save Option</button>
              <button type='button' className="option-close-btn" onClick={() => setShowDialog(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Popup if all fields blank */}
      {showPopup && (
        <div className="option-popup-backdrop">
          <div className="option-popup-box">
            <p>Please enter option text or upload an image before saving.</p>
            <button onClick={() => setShowPopup(false)}>OK</button>
          </div>
        </div>
      )}

      {options.length > 0 && (
        <table className="option-table" ref={optionsTableRef}>
          <thead>
            <tr>
              <th style={{ width: 80 }}>Correct</th>
              <th>Title</th>
              <th>Attachment</th>
            </tr>
          </thead>
          <tbody>
            {options.map((item, index) => (
              <tr key={index}>
                <td style={{ textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={!!item.isCorrect}
                    onChange={(e) => {
                      const updated = options.map((o, i) =>
                        i === index ? { ...o, isCorrect: e.target.checked } : o
                      );
                      setOptions(updated);
                      localStorage.setItem('tempOptions', JSON.stringify(updated));
                      if (onOptionsChange) onOptionsChange(updated);
                    }}
                    style={{ width: 16, height: 16, cursor: "pointer" }}
                  />
                </td>
                <td dangerouslySetInnerHTML={{ __html: item.option }} />
                <td>
                  {item.optionAttachment ? (
                    <img
                      src={item.optionAttachment.startsWith("/uploads/")
                        ? `${process.env.REACT_APP_API_URL}${item.optionAttachment}`
                        : item.optionAttachment}
                      alt="option"
                      style={{ width: 60, height: 40, objectFit: "contain", borderRadius: 4, border: "1px solid #e5e7eb" }}
                    />
                  ) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AddOptionsTable;
